/* A service added to a visit once it is under way, by the technician already
 * doing it, is part of the same turn.
 *
 * Kevin, 2026-10-19: Vic McWherter came in for a Pamper Me Pedicure with Zenny
 * and she sold him a Classic Manicure on top — that is one turn, not one and a
 * half. But a service Zenny does not do, booked with somebody else (a Facial
 * with Ann), is Ann's own turn like any other.
 *
 * Kevin, 2026-10-31: unless it counts as a full turn by itself. Rachel's $65
 * Pamper Me, added to Jocy's Acrylic Fill, is a second piece of real work and a
 * new turn; the $5 Nail Fix added with it stays on the square.
 *
 * "Added once it is under way" is Mangomint's word, not a guess from the book:
 * it reissues every line id on every edit, so a mani-pedi booked that way from
 * the start and one upsold at the chair look the same in the book. Its history
 * does not — each service added carries a time (schema part 16).
 *
 * Pure. Returns the id of the square the line belongs on, or null:
 *
 *   line        { serviceId, fullTurn }  — a booked service with no square of
 *               its own, and whether it counts as a full turn by itself
 *   visitTurns  [{ id, staffId, startedAt, waiting }]  — the visit's squares
 *   added       [{ serviceId, at }]  — services added to the visit, and when
 *   onLine      board technician ids the book has on this line who may do it
 *               (perform its turn type, allowed by its options)
 *
 * The visit started when its first square did; a square still held for a
 * check-in has not started anything. The line belongs on the square of a
 * technician it is booked with, if one of them already has a square on the
 * visit and the service was added after the visit started.
 */
export function addOnHost(line, visitTurns, added, onLine) {
  if (!line || line.serviceId == null || line.fullTurn) return null;
  var live = (visitTurns || []).filter(function (t) { return !t.waiting && t.startedAt != null; });
  if (!live.length) return null;
  var started = Math.min.apply(null, live.map(function (t) { return t.startedAt; }));
  var late = (added || []).some(function (a) {
    return a.serviceId === line.serviceId && a.at > started;
  });
  if (!late) return null;
  var who = onLine || [];
  var host = live.find(function (t) { return who.indexOf(t.staffId) !== -1; });
  return host ? host.id : null;
}

/* A square's extras, moved onto the lines the book has now.
 *
 * Mangomint reissues every line id on every edit. An extra left holding a dead
 * id reads as nothing covering the new line, which then gets folded on again:
 * Rachel's Acrylic square, 2026-10-31, held 12 extras for 2 services. So an extra
 * follows its service, as a square does, to the nearest line of that service on
 * its appointment that nothing has claimed.
 *
 * With no such line, an add-on comes off: it is a stale copy, or the service left
 * the booking. One the desk merged by hand stays, since that was the desk's say.
 * Newest first, so the copy that survives carries the latest times. An extra with
 * no recorded service, or on an appointment not in this book, is left as it is.
 *
 *   extras       [{ appointmentId, appointmentServiceId, sourceServiceId,
 *                   startedAt, addOn }]
 *   linesByAppt  { appointmentId: [{ id, serviceId, startsAt }] }
 *   claimed      { lineId: true } — taken already, and added to here
 *
 * Returns { extras, changed }.
 */
export function relinkExtras(extras, linesByAppt, claimed) {
  var list = extras || [];
  var live = {};
  list.forEach(function (m) {
    var lines = linesByAppt[m.appointmentId];
    if (lines && lines.some(function (l) { return l.id === m.appointmentServiceId; })) {
      live[m.appointmentServiceId] = true;
      claimed[m.appointmentServiceId] = true;
    }
  });

  var changed = false;
  var out = [];
  list.slice().reverse().forEach(function (m) {
    var lines = linesByAppt[m.appointmentId];
    if (!lines || live[m.appointmentServiceId] || m.sourceServiceId == null) {
      out.unshift(m);
      return;
    }
    var open = lines.filter(function (l) {
      return l.serviceId === m.sourceServiceId && !claimed[l.id];
    }).sort(function (a, b) {
      return Math.abs(a.startsAt - m.startedAt) - Math.abs(b.startsAt - m.startedAt);
    });
    if (open.length) {
      claimed[open[0].id] = true;
      out.unshift(Object.assign({}, m, { appointmentServiceId: open[0].id }));
      changed = true;
    } else if (m.addOn) {
      changed = true;
    } else {
      out.unshift(m);
    }
  });
  return { extras: out, changed: changed };
}
